// Hooks from react-use, in-sourced because react-use doesn't support ES modules.

import {DependencyList, useCallback, useEffect, useRef, useState} from "react";

function useMountedState(): () => boolean {
    const mountedRef = useRef(false);
    const get = useCallback(() => mountedRef.current, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    return get;
}

// useAsyncFn

type AsyncState<T> =
    | {loading: boolean; error?: undefined; value?: undefined}
    | {loading: true; error?: Error | undefined; value?: T}
    | {loading: false; error: Error; value?: undefined}
    | {loading: false; error?: undefined; value: T};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FunctionReturningPromise = (...args: never[]) => Promise<any>;

type PromiseType<T> = T extends Promise<infer U> ? U : never;

type StateFromFn<T extends FunctionReturningPromise> = AsyncState<
    PromiseType<ReturnType<T>>
>;

export function useAsyncFn<T extends FunctionReturningPromise>(
    fn: T,
    deps: DependencyList = [],
    initialState: StateFromFn<T> = {loading: false},
): [StateFromFn<T>, T] {
    const lastCallId = useRef(0);
    const isMounted = useMountedState();
    const [state, set] = useState<StateFromFn<T>>(initialState);

    const callback = useCallback((...args: Parameters<T>): ReturnType<T> => {
        const callId = ++lastCallId.current;

        if (!state.loading) {
            set((prevState) => ({...prevState, loading: true}));
        }

        return fn(...args).then(
            (value: PromiseType<ReturnType<T>>) => {
                if (isMounted() && callId === lastCallId.current) {
                    set({value, loading: false});
                }
                return value;
            },
            (error: Error) => {
                if (isMounted() && callId === lastCallId.current) {
                    set({error, loading: false});
                }
                return error;
            },
        ) as ReturnType<T>;
    }, deps);

    return [state, callback as unknown as T];
}

// useAsync

export function useAsync<T extends FunctionReturningPromise>(
    fn: T,
    deps: DependencyList = [],
) {
    const [state, callback] = useAsyncFn(fn, deps, {
        loading: true,
    });

    useEffect(() => {
        (callback as () => void)();
    }, [callback]);

    return state;
}

// useTimeoutFn

export type UseTimeoutFnReturn = [() => boolean | null, () => void, () => void];

export function useTimeoutFn(
    fn: () => void | Promise<void>,
    ms: number = 0,
): UseTimeoutFnReturn {
    const ready = useRef<boolean | null>(false);
    const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const callback = useRef(fn);

    const isReady = useCallback(() => ready.current, []);

    const set = useCallback(() => {
        ready.current = false;
        if (timeout.current != null) {
            clearTimeout(timeout.current);
        }

        timeout.current = setTimeout(() => {
            ready.current = true;
            void callback.current();
        }, ms);
    }, [ms]);

    const clear = useCallback(() => {
        ready.current = null;
        if (timeout.current != null) {
            clearTimeout(timeout.current);
        }
    }, []);

    useEffect(() => {
        callback.current = fn;
    }, [fn]);

    useEffect(() => {
        set();
        return clear;
    }, [ms]);

    return [isReady, clear, set];
}

// useDebounce

export type UseDebounceReturn = [() => boolean | null, () => void];

export function useDebounce(
    fn: () => void | Promise<void>,
    ms: number = 0,
    deps: DependencyList = [],
): UseDebounceReturn {
    const [isReady, cancel, reset] = useTimeoutFn(fn, ms);

    useEffect(reset, deps);

    return [isReady, cancel];
}

// useSerializedSave

// A debounced save whose requests never overlap: each save waits for the
// previous one, so whole-snapshot writes reach the server in the order they
// were made. flush() cancels the pending timer and saves now, resolving once
// every queued save has landed.
export function useSerializedSave(
    save: () => Promise<void>,
    ms: number = 0,
    deps: DependencyList = [],
): {flush: () => Promise<void>} {
    const chain = useRef<Promise<void>>(Promise.resolve());
    const latest = useRef(save);

    useEffect(() => {
        latest.current = save;
    });

    const enqueue = useCallback(() => {
        const next = chain.current.then(() => latest.current());
        chain.current = next.catch(() => {});
        return next;
    }, []);

    const [, cancel] = useDebounce(() => void enqueue(), ms, deps);

    const flush = useCallback(() => {
        cancel();
        return enqueue();
    }, [cancel, enqueue]);

    return {flush};
}

// useMedia

export function useMedia(query: string, defaultState?: boolean): boolean {
    const [state, setState] = useState(() => {
        if (defaultState !== undefined) {
            return defaultState;
        }
        if (typeof window !== "undefined") {
            return window.matchMedia(query).matches;
        }
        return false;
    });

    useEffect(() => {
        let mounted = true;
        const mql = window.matchMedia(query);
        const onChange = () => {
            if (!mounted) {
                return;
            }
            setState(!!mql.matches);
        };

        mql.addEventListener("change", onChange);
        setState(mql.matches);

        return () => {
            mounted = false;
            mql.removeEventListener("change", onChange);
        };
    }, [query]);

    return state;
}
